<template>
  <div class="mt-3">
    <TopSection :title="$t('distributions') | capitalize">
      <template v-slot:filters>
        <div class="d-flex align-items-center ml-4">
          <div class="d-flex align-items-center mr-4">
            <toggle-button
              v-model="showHiddenDistributions"
              color="#2ec775"
            />
            <div class="ml-2">
              {{ $t('show_hidden') | capitalize }}
            </div>
          </div>
          <b-button
            size="lg"
            variant="primary"
            @click="$router.push({ name: 'distribution-create' })"
          >
            {{ $t('newDistribution') | capitalize }}
          </b-button>
        </div>
      </template>
    </TopSection>

    <!-- TABLE -->
    <v-server-table
      ref="distributions"
      url="distributions/"
      :columns="columns"
      :options="options"
      @pagination="onPagination"
      @filter="onFilter"
    >
      <!-- NAME COLUMN -->
      <template v-slot:name="{ row }">
        <div
          class="link-class"
          @click="$router.push({ name: 'distribution-detail', params: { id: row.id } })"
        >
          {{ getFileName(row.fund_file.path) }}
        </div>
      </template>

      <template v-slot:status="{ row }">
        {{ row.status | capitalize }}
      </template>

      <!-- UPLOAD DATE COLUMN -->
      <template v-slot:time_period="{ row }">
        <div class="d-flex flex-column text-nowrap">
          <div v-if="row.start_date" class="d-flex align-items-end">
            <span class="table-sub-class pr-2">{{ $t("from") }}</span>
            <div class="table-main-class">
              {{ row.start_date | transformDate("MMMM Do YYYY") }}
            </div>
          </div>
          <div v-if="row.end_date" class="d-flex align-items-end">
            <span class="table-sub-class pr-2">{{ $t("to") }}</span>
            <div class="table-main-class">
              {{ row.end_date | transformDate("MMMM Do YYYY") }}
            </div>
          </div>
        </div>
      </template>

      <!-- ALLOCATED % COLUMN -->
      <template v-slot:claimed="{ row }">
        <div v-if="row.stats_percentages.claimed_amount" class="pt-2">
          <b-progress
            style="height:9px; width:95px;"
            :value="row.stats_percentages.claimed_amount.toFixed(2) | getPercentageValue"
            :max="100"
          />
          <div class="table-sub-class m-1">
            {{ `${ row.stats_percentages.claimed_amount.toFixed(2)} %` }}
          </div>
        </div>
      </template>

      <!-- UNCLAIMED % COLUMN -->
      <template v-slot:unclaimed="{ row }">
        <div v-if="row.stats_percentages.unclaimed_amount" class="pt-2">
          <b-progress
            variant="danger"
            style="height:9px; width:95px;"
            :value="row.stats_percentages.unclaimed_amount.toFixed(2) | getPercentageValue"
            :max="100"
          />
          <div class="table-sub-class m-1">
            {{ `${ row.stats_percentages.unclaimed_amount.toFixed(2)} %` }}
          </div>
        </div>
      </template>

      <!-- UNMATCHED % COLUMN -->
      <template v-slot:unmatched="{ row }">
        <div v-if="row.stats_percentages.unmatched_amount" class="pt-2">
          <b-progress
            variant="danger"
            style="height:9px; width:95px;"
            :value="row.stats_percentages.unmatched_amount.toFixed(2) | getPercentageValue"
            :max="100"
          />
          <div class="table-sub-class m-1">
            {{ `${ row.stats_percentages.unmatched_amount.toFixed(2)} %` }}
          </div>
        </div>
      </template>

      <!-- OVERCLAIMED % COLUMN -->
      <template v-slot:overclaimed="{ row }">
        <div v-if="row.stats_percentages.overclaimed_amount" class="pt-2">
          <b-progress
            variant="danger"
            style="height:9px; width:95px;"
            :value="row.stats_percentages.overclaimed_amount.toFixed(2) | getPercentageValue"
            :max="100"
          />
          <div class="table-sub-class m-1">
            {{ `${ row.stats_percentages.overclaimed_amount.toFixed(2)} %` }}
          </div>
        </div>
      </template>

      <!-- UNDERCLAIMED % COLUMN -->
      <template v-slot:underclaimed="{ row }">
        <div v-if="row.stats_percentages.underclaimed_amount" class="pt-2">
          <b-progress
            variant="danger"
            style="height:9px; width:95px;"
            :value="row.stats_percentages.underclaimed_amount.toFixed(2) | getPercentageValue"
            :max="100"
          />
          <div class="table-sub-class m-1">
            {{ `${ row.stats_percentages.underclaimed_amount.toFixed(2)} %` }}
          </div>
        </div>
      </template>

      <!-- ACTIONS COLUMN -->
      <template v-slot:actions="{ row }">
        <div class="d-flex align-items-center">
          <div
            v-b-tooltip.hover
            :title="$t('re_allocate')"
            class="table-sub-class text-center medium clickable mr-4"
            :class="['done', 'error'].includes(row.status) ? '' : 'disabled'"
            @click="['done', 'error'].includes(row.status) ? reallocate(row) : null"
          >
            <i class="far fa-sync" />
          </div>
          <div
            v-b-tooltip.hover
            class="table-sub-class text-center medium clickable mr-4"
            :title="$t('download')"
            @click="downloadFiles(row)"
          >
            <i class="far fa-file-download" />
          </div>
          <div
            v-b-tooltip.hover
            :title="$t('validate')"
            class="table-sub-class text-center medium clickable mr-4"
            :class="isDone(row)? '': 'disabled'"
            @click="onValidate(row)"
          >
            <i class="far fa-check" />
          </div>
          <div
            v-b-tooltip.hover
            :title="$t('delete')"
            class="table-sub-class text-center medium clickable mr-4"
            @click="deleteDistribution(row)"
            v-if="canBeDeleted(row)"
          >
            <i class="far fa-times" />
          </div>
          <div
            v-b-tooltip.hover
            :title="$t('history')"
            class="table-sub-class text-center medium clickable mr-4"
            @click="showHistory(row)"
          >
            <i class="fas fa-history" />
          </div>
          <div
            v-b-tooltip.hover
            :title="isHidden(row.id) ? $t('show'): $t('hide')"
            class="table-sub-class text-center medium clickable mr-4"
            @click="toggleVisibility(row)"
          >
            <i class="fas fa-eye-slash" v-if="isHidden(row.id)" />
            <i class="fas fa-eye" v-else />
          </div>
        </div>
      </template>
    </v-server-table>
    <!-- Validation -->
    <b-modal
      id="validateConfirmation"
      ref="validateConfirmation"
      :title="`${$t('confirm_validate')}: ${selectedDistributionFileName}` | capitalize"
      hide-footer
    >
      <p>{{ $t('confirm_finalize_distribution') }}</p>
      <div class="flex justify-end">
        <action-button
          variant="primary"
          size="lg"
          @click="onConfirmValidate()"
        >
          {{ $t('yes') | upper }}
        </action-button>

        <b-button
          size="lg"
          class="ml-3"
          variant="outline-primary"
          @click="onCancelValidate"
        >
          {{ $t('no') | upper }}
        </b-button>
      </div>
    </b-modal>

    <!-- History -->
    <b-modal
      id="historyModal"
      ref="historyModal"
      hide-footer
      hide-header
      size="xl"
    >
      <b-table-lite class="modal-table" :fields="historyFields" :items="history" borderless>
        <template v-slot:cell(created_by)="{ item }">
          <span v-if="item.created_by">
            {{ item.created_by.name_and_last_name }}
          </span>
        </template>
        <template v-slot:cell(created)="{ item }">
          <div>
            {{ item.created | transformDate("MMMM Do YYYY") }}
          </div>
          <div class="table-sub-class text-sm">
            {{ item.created | transformDate("h:mm a") }}
          </div>
        </template>
      </b-table-lite>
    </b-modal>

    <!-- Delete Distribution Confirmation -->
    <b-modal
      id="confirmDeleteDistribution"
      ref="confirmDeleteDistribution"
      :title="$t('delete_distribution') | capitalize"
      hide-footer
    >
      <p>{{ $t("delete_distribution_warning") }}</p>
      <div class="flex justify-end">
        <action-button
          variant="primary"
          size="big"
          @click="confirmDeletion"
        >
          {{ $t("delete_distribution") | capitalize }}
        </action-button>
      </div>
    </b-modal>

    <!-- Re-allocation Rematch Confirmation -->
    <b-modal
      id="reallocateRematchModal"
      ref="reallocateRematchModal"
      :title="$t('playlist_rematching') | capitalize"
      hide-footer
    >
      <p>
        {{ $t('playlist_rematching_desc') }}
      </p>

      <b-form-group class="mt-3">
        <b-form-checkbox v-model="rematchPlaylists">
          {{ $t('playlist_rematching_check') }}
        </b-form-checkbox>
      </b-form-group>

      <div class="d-flex justify-content-end mt-4">
        <b-button
          size="lg"
          variant="outline-secondary"
          @click="$refs.reallocateRematchModal.hide()"
        >
          {{ $t('cancel') | capitalize }}
        </b-button>
        <action-button
          size="lg"
          variant="primary"
          class="ml-3"
          @click="confirmReallocate"
        >
          {{ $t('accept') | capitalize }}
        </action-button>
      </div>
    </b-modal>

    <!-- The target of the link is the iframe, given by its name -->
    <div style="display:none;">
      <iframe
        v-for="link in downloadLinks"
        :key="link"
        :name="link"
      >
        <a :target="link" :href="link" :id="link" download>{{ link }}</a>
      </iframe>
    </div>
  </div>
</template>

<style scoped>
::v-deep .table .disabled {
  color: lightgray !important;
  cursor: not-allowed;
}

.modal-body > p {
  font-size: 1.1rem;
}
</style>

<script>
import { capitalize, get } from "lodash"
import {
  doRequest,
  responsePaginatedAdapter
} from "@/assets/libs/utils"
import { mapGetters, mapState } from "vuex"
import { Event } from "vue-tables-2"
import { api } from "@/main"
import { downloadFile } from "@/assets/libs/services"
import { listRouteMixin } from "@/utils/mixins"

export default {
  name: "DistributionPage",
  mixins: [listRouteMixin],

  data () {
    return {
      selectedDistribution: null,
      distributionToBeDeleted: null,
      downloadLinks: [],
      hiddenList: [],
      columns: [
        "name",
        "time_period",
        "status",
        "claimed",
        "unclaimed",
        "unmatched",
        "overclaimed",
        "underclaimed",
        "actions"
      ],
      history: [],
      historyFields: [
        { key: "version", label: capitalize(this.$t("version")) },
        { key: "created_by", label: capitalize(this.$t("agent")) },
        { key: "created", label: capitalize(this.$t("date")) },
        { key: "reason", label: capitalize(this.$t("action")) },
      ],
      showHiddenDistributions: false,
      rematchPlaylists: false,
      distributionPendingReallocate: null,
      options: {
        sortable: [],
        filterable: true,
        customFilters: ["hidden"],
        initFilters: { "hidden": "" },
        headings: {
          name: capitalize(this.$t("name")),
          time_period: capitalize(this.$t("time_period")),
          status: capitalize(this.$t("status")),
          claimed: capitalize(this.$t("claimed")) + "%",
          unclaimed: capitalize(this.$t("unclaimed")) + "%",
          unmatched: capitalize(this.$t("unmatched")) + "%",
          overclaimed: capitalize(this.$t("overclaimed")) + "%",
          underclaimed: capitalize(this.$t("underclaimed")) + "%",
          actions: ""
        },
        requestFunction (params) {
          const queryParams = { ...params, ...this.$route.query }
          this.page = Number(get(queryParams, "page", 1))

          return doRequest(this.url, queryParams)
        },
        responseAdapter: resp => responsePaginatedAdapter(resp),
      },
    }
  },
  methods: {
    getData () {
      this.$refs.distributions.getData()
    },
    getFileName (str) {
      return str.split("/").pop()
    },
    isHidden (id) {
      return this.hiddenList.includes(id)
    },
    retrieveHiddenList () {
      let savedhiddenDistributions = localStorage.getItem("hiddenDistributions") || "[]"
      let hidden = JSON.parse(savedhiddenDistributions)
      return hidden
    },
    updateHiddenList (id) {
      const hiddenList = this.retrieveHiddenList()

      if (hiddenList.includes(id) && this.isHidden(id)) {
        let updatedList = [ ...hiddenList ].filter(item => item !== id)
        localStorage.setItem("hiddenDistributions", JSON.stringify(updatedList))
      } else {
        localStorage.setItem("hiddenDistributions", JSON.stringify(hiddenList.concat(id)))
      }
      return this.retrieveHiddenList()
    },
    toggleVisibility (row) {
      this.hiddenList = this.updateHiddenList(row.id)
    },
    showErrors (errors) {
      errors.forEach(msg => { this.$toasted.global.toast(msg) })
    },
    postShowAndRefresh (url, msg) {
      api
        .post(url)
        .then(() => {
          this.$toasted.global.toast(msg)
          this.$refs.distributions.refresh()
        })
        .catch(err => {
          if (err.response && err.response.data) {
            this.showErrors(err.response.data)
          } else {
            this.$toasted.global.toast(err)
          }
        })
    },
    reallocate (row) {
      this.distributionPendingReallocate = row
      this.rematchPlaylists = false
      this.$refs.reallocateRematchModal.show()
    },
    confirmReallocate () {
      this.$refs.reallocateRematchModal.hide()
      const id = this.distributionPendingReallocate.id

      api
        .post(`distributions/${id}/reallocate/`, { rematch_playlists: this.rematchPlaylists })
        .then(() => {
          this.$toasted.global.toast("Distribution Reallocated!")
          this.$refs.distributions.refresh()
          this.distributionPendingReallocate = null
        })
        .catch(err => {
          if (err.response && err.response.data) {
            this.showErrors(err.response.data)
          } else {
            this.$toasted.global.toast(err)
          }
        })
    },
    validate () {
      this.postShowAndRefresh(
        `distributions/${this.selectedDistribution.id}/distributed/`,
        "Distribution Validated!"
      )
    },
    onCancelValidate () {
      this.$bvModal.hide("validateConfirmation")
    },
    onConfirmValidate () {
      this.validate()
      this.$bvModal.hide("validateConfirmation")
    },
    onValidate (row) {
      if(this.isDone(row)) {
        this.selectedDistribution = row
        this.$bvModal.show("validateConfirmation")
      }
    },
    deleteDistribution ({ id }) {
      this.distributionToBeDeleted = id
      this.$bvModal.show("confirmDeleteDistribution")
    },
    confirmDeletion () {
      this.$bvModal.hide("confirmDeleteDistribution")
      api
        .delete(`distributions/${this.distributionToBeDeleted}/`)
        .then(() => {
          this.$toasted.global.toast(this.$t("distribution_deleted"))
          this.distributionToBeDeleted = null
          this.$refs.distributions.refresh()
        })
        .catch(err => {
          if (err.response && err.response.data) {
            this.showErrors(err.response.data)
          } else {
            this.$toasted.global.toast(err)
          }
        })
    },
    showHistory (distribution) {
      this.history = distribution.history
      this.$refs.historyModal.show()
    },
    clickOnDownLoadLink (downloadLink) {
      this.downloadLinks = [downloadLink]
      this.$nextTick(() => {
        const link = document.getElementById(downloadLink)
        link.click()
      })
    },
    downloadFiles ({ id }) {
      api
        .get(`distributions/${id}/download/`)
        .then(res => {
          const downloadLink = res.data
          try {
            this.clickOnDownLoadLink(downloadLink)
          } catch (error) {
            this.$toasted.global.toast(error)
          }
        })
        .catch(err => {
          if (err.response && err.response.data) {
            this.showErrors(err.response.data)
          } else {
            this.$toasted.global.toast(err)
          }
        })
    },
    downloadReport ({ id }) {
      api
        .get(`distributions/${id}/undistributed/`)
        .then(res => {
          try {
            downloadFile(res.data, "undistributed.csv")
          } catch (error) {
            this.$toasted.global.toast(error)
          }
        })
        .catch(err => {
          if (err.response && err.response.data) {
            this.showErrors(err.response.data)
          } else {
            this.$toasted.global.toast(err)
          }
        })
    },
    canBeDeleted (distribution) {
      return distribution.status === this.statusTypes.DRAFT
    },
    isDone (distribution) {
      return distribution.status === this.statusTypes.DONE
    },
    onFilter (search) {
      this.options.previousPage = null
      this.$router.replace({ query: { ...this.$route.query, fuzzy_search: search, page: 1 } })
    },
  },
  computed: {
    ...mapGetters("soundsys", ["statusTypes"]),
    ...mapState("soundsys", ["rights"]),
    selectedDistributionFileName () {
      return this.selectedDistribution ? this.getFileName(this.selectedDistribution.fund_file.path): ""
    }
  },
  watch: {
    hiddenList (value) {
      let hidden = this.showHiddenDistributions ? "" : value.toString()
      Event.$emit("vue-tables.filter::hidden", hidden)
    },
    showHiddenDistributions (show) {
      let hidden = show ? "" : this.hiddenList.toString()
      if (!show) this.$refs.distributions.setPage(1)
      Event.$emit("vue-tables.filter::hidden", hidden)
    }
  },
  mounted () {
    this.hiddenList = this.retrieveHiddenList()
  }
}
</script>
